import Application from "../../../../types/admin/Application";
import { useEffect, useState } from "react";
import axios from "axios";
import useSWR from "swr";
import { EmailPreviewer } from "../../EmailTemplates";
import { EmailTemplate, EmailTemplateViewMode } from "../../../../types/admin/EmailTemplate";
import { RequestError } from "../../../../types/Error";
import { fetcher } from "../../../../util/request";
import Picker from "../../../../components/aahk/configManager/components/Picker";
import ErrorBoundary from "../../../../components/wrappers/ErrorBoundary";
import { Button } from "semantic-ui-react";
import EmailSend from "./email/EmailSend";
import EmailHistory from "./email/EmailHistory";

interface EmailSchedulerProps {
    application: Application;
}

const EmailPreview: React.FC<{ email: EmailTemplate | undefined; emailId: string }> = ({ email, emailId }) => {
    if (emailId === "") return null;

    return (
        <div className="border border-info rounded border-1 p-3">
            <EmailPreviewer values={email} />
        </div>
    );
};

const EmailScheduler: React.FC<EmailSchedulerProps> = ({ application }) => {
    const { data: emails, error, isLoading } = useSWR<EmailTemplate[], RequestError>(
        '/admin/email-template/list',
        fetcher
    );

    const [state, setState] = useState({
        emailId: "",
        email: undefined as EmailTemplate | undefined,
        mode: "view" as EmailTemplateViewMode
    });

    useEffect(() => {
        if (!state.emailId) return;

        const fetchEmailTemplate = async () => {
            try {
                const response = await axios.post(
                    `/admin/email-template/preview/${application.id}`,
                    { emailId: state.emailId }
                );
                setState(prev => ({ ...prev, email: response.data }));
            } catch (error) {
                console.error("Error fetching email template:", error);
                setState(prev => ({ ...prev, email: undefined }));
            }
        };

        fetchEmailTemplate();
    }, [state.emailId, application.id]);

    const handleReset = () => setState({
        emailId: "",
        email: undefined,
        mode: "view"
    });

    if (state.mode === "send") {
        return <EmailSend emailTemplate={state.email!} application={application} goBack={handleReset} />;
    }

    if (state.mode === "history") {
        return <EmailHistory applicationId={application.id} goBack={handleReset} />;
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center">
                <Picker
                    loading={isLoading}
                    options={(emails ?? []).map(({ id }) => id)}
                    onChange={emailId => setState(prev => ({ ...prev, emailId }))}
                    placeholder="Select an email template"
                    popupMatchSelectWidth={false}
                    style={{
                        minWidth: 300,
                        width: "fit-content",
                        maxWidth: "100%",
                    }}
                />
                <div className="d-flex justify-content-between gap-2">
                    <Button
                        size="small"
                        color="purple"
                        onClick={() => setState(prev => ({ ...prev, mode: "history" }))}
                        content="View past emails"
                        icon="eye"
                        className="h-25"
                    />
                    <Button
                        size="small"
                        color="green"
                        onClick={() => setState(prev => ({ ...prev, mode: "send" }))}
                        content="Edit Draft & Send"
                        icon="send"
                        className="h-25"
                        disabled={!state.emailId}
                    />
                </div>
            </div>
            <ErrorBoundary>
                <EmailPreview email={state.email} emailId={state.emailId} />
            </ErrorBoundary>
        </>
    );
};

export default EmailScheduler;
